import { updateProfileExperience } from "@peerspace/bellhop";
import {
  mutationOptions,
  QueryClient,
  queryOptions,
} from "@tanstack/react-query";

import { getQueryClient } from "~/lib/react-query/query-client";
import { profileKeys } from "~/queries/profile/profile.keys";
import {
  fetchProfile,
  fetchProfileExperiences,
  patchProfileCache,
  requestEmailVerificationCode,
  requestPhoneVerificationCode,
  updateEmailSubscriptionService,
  updateProfileData,
  verifyProfileEmail,
  verifyProfilePhone,
} from "~/queries/profile/profile.service";
import { NormalizedProfile, ProfileData } from "~/types/profile.types";

/**
 * The single source of truth for every profile server-state operation. Keep all
 * profile reads under `queries` (fed to `useQuery`) and all writes under
 * `mutations` (fed to `useMutation`) so they stay co-located and easy to find.
 */
const profileOptions = {
  mutations: {
    update: () =>
      mutationOptions({
        mutationFn: updateProfileData,
        onSuccess: ({ profile }) => patchProfileCache(profile),
      }),
    updateEmailSubscription: (queryClient: QueryClient) =>
      mutationOptions({
        mutationFn: updateEmailSubscriptionService,
        // we are adding a onMutate so the checkbox can show as "changed" since the value is coming from state
        onMutate: async (
          subscribed,
        ): Promise<{ email_unsubscribes: { all: boolean } }> => {
          await queryClient.cancelQueries({ queryKey: profileKeys.profile });

          const previous = queryClient.getQueryData<NormalizedProfile>(
            profileKeys.profile,
          );

          queryClient.setQueryData<NormalizedProfile | undefined>(
            profileKeys.profile,
            (current) =>
              current && {
                ...current,
                emailUnsubscribes: {
                  ...current.emailUnsubscribes,
                  all: !subscribed,
                },
              },
          );

          // this will be used in onError
          return {
            email_unsubscribes: { all: !!previous?.emailUnsubscribes?.all },
          };
        },
        onSuccess: (subscribed: boolean) =>
          patchProfileCache({ email_unsubscribes: { all: !subscribed } }),
        onError: (_error, _subscribed, previousVal) => {
          if (previousVal) {
            patchProfileCache(previousVal);
          }
        },
      }),
    verifyEmail: () =>
      mutationOptions({
        mutationFn: verifyProfileEmail,
        onSuccess: () => {
          patchProfileCache({ email_verified: true });
          // The verification_id is single-use — drop it so a stale id can't leak
          // into a later verify attempt.
          getQueryClient().removeQueries({
            queryKey: profileKeys.emailVerification,
          });
        },
      }),
    verifyPhone: () =>
      mutationOptions({
        mutationFn: verifyProfilePhone,
        onSuccess: () => {
          patchProfileCache({ phone_verified: true });
          // The verification_id is single-use — drop it so a stale id can't leak
          // into a later verify attempt.
          getQueryClient().removeQueries({
            queryKey: profileKeys.phoneVerification,
          });
        },
      }),
    sendPhoneVerificationCode: () =>
      mutationOptions({
        mutationFn: requestPhoneVerificationCode,
        onSuccess: (data) =>
          getQueryClient().setQueryData(profileKeys.phoneVerification, data),
      }),
    sendEmailVerificationCode: () =>
      mutationOptions({
        mutationFn: requestEmailVerificationCode,
        onSuccess: (data) =>
          getQueryClient().setQueryData(profileKeys.emailVerification, data),
      }),
    /**
     * Accepts or declines a pending profile experience (e.g. updated terms).
     * Invalidates the experiences cache on success. Pass `refetchOnSuccess:
     * false` to mark the cache stale without refetching — useful when the
     * caller is about to navigate away and wants to keep current UI mounted.
     */
    updateExperience: () =>
      mutationOptions({
        mutationFn: ({
          completeExp,
          experienceId,
        }: {
          completeExp: boolean;
          experienceId: string;
          refetchOnSuccess?: boolean;
        }) =>
          updateProfileExperience(
            { experienceId },
            { complete_exp: completeExp },
          ),
        onSuccess: (_data, { refetchOnSuccess = true }) => {
          getQueryClient().invalidateQueries({
            queryKey: profileKeys.experiences,
            ...(refetchOnSuccess ? {} : { refetchType: "none" as const }),
          });
        },
      }),
  },
  queries: {
    experiences: () =>
      queryOptions({
        queryFn: fetchProfileExperiences,
        queryKey: profileKeys.experiences,
      }),
    me: <TSelected = ProfileData>(select?: (data: ProfileData) => TSelected) =>
      queryOptions({
        queryFn: fetchProfile,
        queryKey: profileKeys.profile,
        select,
      }),
  },
};

export { profileOptions };
